import { useCallback } from 'react';
import type { Nullable } from 'corgi-types';

import { EventBusEvent } from 'common/enums/EventBus';
import eventBus from 'common/event-bus/EventBus';
import { useLocalization } from 'common/providers/LocaleProvider';
import { dispatchTrackingEventGA4 } from 'common/tracking/dispatchTrackingEventGA4';
import type { GalleryTrackingProps } from 'common/tracking/productGallery';
import { OverlayInteraction, OverlayStatus } from 'common/types/tracking';
import { getEnterHandler } from 'common/utils/keyboard';

export interface Model3DData {
  path: string;
}

const openOverlay = (
  data: Model3DData,
  buttonLabel: string,
  trackingProps?: GalleryTrackingProps
) => {
  eventBus.emit<string>(EventBusEvent.OpenModel3D, data.path);

  const { productGalleryV2, isOverlayOpen } = trackingProps ?? {};

  const overlayEventProps = productGalleryV2
    ? {
        element: 'tab gallery button',
        button_text: '3D',
        overlay: isOverlayOpen ? OverlayStatus.Open : OverlayStatus.Closed,
      }
    : {
        element: 'button',
        button_text: buttonLabel,
      };

  dispatchTrackingEventGA4({
    event: 'product-gallery_click',
    interaction: OverlayInteraction.Open,
    ...overlayEventProps,
  });
};

export const useButton3D = (
  model3DData: Nullable<Model3DData>,
  trackingProps?: GalleryTrackingProps
) => {
  const { t } = useLocalization();
  const label = t('h24_3d_view');
  const handleClick = () => {
    if (model3DData) {
      openOverlay(model3DData, label, trackingProps);
    }
  };
  const handleKeyDown = useCallback(() => getEnterHandler(handleClick), [handleClick]);

  return { label, onClick: handleClick, onKeyDown: handleKeyDown };
};
